import { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CalendarOff, Plus, CheckCircle2, XCircle, Clock, AlertCircle, FileText } from 'lucide-react';
import { useCurrentUser } from '@/hooks/useCurrentUser';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  approved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  rejected: 'bg-rose-100 text-rose-800 border-rose-200',
  cancelled: 'bg-slate-100 text-slate-600 border-slate-200',
};
const STATUS_ICON = { pending: Clock, approved: CheckCircle2, rejected: XCircle, cancelled: AlertCircle };

export default function MyLeave() {
  const user = useCurrentUser();
  const role = user?.role;
  const isReviewer = role === 'super_admin' || role === 'admin' || role === 'service_manager' || role === 'floor_manager';
  const isOwner = role === 'super_admin' || role === 'admin';
  const [tab, setTab] = useState(isReviewer ? 'review' : 'mine');

  return (
    <Layout user={user}>
      <div className="space-y-6 max-w-6xl" data-testid="leave-page">
        <div className="flex items-center gap-3">
          <CalendarOff className="h-7 w-7 text-purple-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Leave Management</h1>
            <p className="text-sm text-slate-500">Apply for leave, see balance and status</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className={`grid ${isReviewer ? 'grid-cols-3 max-w-xl' : 'grid-cols-1 max-w-xs'}`} data-testid="leave-tabs">
            <TabsTrigger value="mine" data-testid="leave-tab-mine">My Leave</TabsTrigger>
            {isReviewer && <TabsTrigger value="review" data-testid="leave-tab-review">Review Requests</TabsTrigger>}
            {isOwner && <TabsTrigger value="balances" data-testid="leave-tab-balances">All Balances</TabsTrigger>}
          </TabsList>

          <TabsContent value="mine" className="mt-6"><MyLeaveTab user={user} /></TabsContent>
          {isReviewer && <TabsContent value="review" className="mt-6"><ReviewQueueTab role={role} /></TabsContent>}
          {isOwner && <TabsContent value="balances" className="mt-6"><BalancesTab /></TabsContent>}
        </Tabs>
      </div>
    </Layout>
  );
}

function MyLeaveTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApply, setShowApply] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/leave/my`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !data) return <Card className="p-6">Loading…</Card>;
  const { allocation, used, remaining, history } = data;

  const handleCancel = async (request_id) => {
    if (!confirm('Cancel this leave request?')) return;
    const res = await fetch(`${BACKEND_URL}/api/leave/${request_id}/cancel`, { method: 'POST', credentials: 'include' });
    if (res.ok) { toast.success('Cancelled'); fetchData(); } else toast.error('Failed');
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <BalanceCard
          color="purple" label="Annual Leave"
          total={allocation.annual_accrued_ytd}
          used={used.annual} remaining={remaining.annual}
          subline={`${allocation.annual_per_week}h/wk accrual · ${allocation.weeks_elapsed_in_year}w earned`}
          dataTestid="my-annual-balance"
        />
        <BalanceCard
          color="blue" label="Sick Leave"
          total={allocation.sick_accrued_ytd}
          used={used.sick} remaining={remaining.sick}
          subline={`${allocation.sick_per_week}h/wk accrual`}
          dataTestid="my-sick-balance"
        />
        <BalanceCard
          color="amber" label="Long Service"
          total={allocation.long_service_accrued_ytd}
          used={used.long_service} remaining={remaining.long_service}
          subline={`${allocation.long_service_per_week}h/wk accrual`}
          dataTestid="my-lsl-balance"
        />
        <BalanceCard
          color="slate" label="Unpaid Leave" total={null}
          used={used.unpaid} remaining={null}
          subline="No cap — manager approval required"
          dataTestid="my-unpaid-balance"
        />
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">My Requests</h3>
          <Button onClick={() => setShowApply(true)} data-testid="apply-leave-btn">
            <Plus className="h-4 w-4 mr-1" /> Apply for leave
          </Button>
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">No leave requests yet.</p>
        ) : (
          <div className="space-y-2">
            {history.map(r => {
              const Icon = STATUS_ICON[r.status] || Clock;
              return (
                <div key={r.request_id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`leave-row-${r.request_id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border flex items-center gap-1 ${STATUS_BADGE[r.status]}`}>
                        <Icon className="h-3 w-3" /> {r.status}
                      </span>
                      <span className="font-semibold text-sm capitalize">{r.leave_type}</span>
                      <span className="text-xs text-slate-500">{r.start_date} → {r.end_date} · {r.hours}h</span>
                    </div>
                    {r.reason && <p className="text-xs text-slate-600 mt-1 italic">"{r.reason}"</p>}
                    {r.review_comments && <p className="text-xs text-blue-700 mt-1">💬 {r.reviewer_name}: {r.review_comments}</p>}
                  </div>
                  {r.status === 'pending' && (
                    <Button size="sm" variant="outline" onClick={() => handleCancel(r.request_id)} data-testid={`cancel-leave-${r.request_id}`}>Cancel</Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <ApplyLeaveDialog open={showApply} onClose={() => setShowApply(false)} onSaved={fetchData} />
    </div>
  );
}

function BalanceCard({ color, label, total, used, remaining, subline, dataTestid }) {
  const colorMap = {
    purple: { border: 'border-l-purple-500', bg: 'bg-purple-50', text: 'text-purple-900', sub: 'text-purple-600', bar: 'bg-purple-500' },
    blue:   { border: 'border-l-blue-500',   bg: 'bg-blue-50',   text: 'text-blue-900',   sub: 'text-blue-600',   bar: 'bg-blue-500' },
    amber:  { border: 'border-l-amber-500',  bg: 'bg-amber-50',  text: 'text-amber-900',  sub: 'text-amber-600',  bar: 'bg-amber-500' },
    slate:  { border: 'border-l-slate-500',  bg: 'bg-slate-50',  text: 'text-slate-900',  sub: 'text-slate-600',  bar: 'bg-slate-500' },
  }[color] || { border: 'border-l-slate-300', bg: 'bg-slate-50', text: 'text-slate-900', sub: 'text-slate-600', bar: 'bg-slate-500' };
  return (
    <Card className={`p-4 border-l-4 ${colorMap.border} ${colorMap.bg}`} data-testid={dataTestid}>
      <p className={`text-xs uppercase tracking-wider font-bold ${colorMap.sub}`}>{label}</p>
      <p className={`text-3xl font-black mt-1 ${colorMap.text}`}>
        {remaining != null ? `${remaining.toFixed(0)}h` : `${used.toFixed(0)}h used`}
      </p>
      {total != null && <p className={`text-xs mt-1 ${colorMap.sub}`}>{used.toFixed(0)}h used of {total.toFixed(1)}h accrued</p>}
      <p className="text-[10px] text-slate-500 mt-2">{subline}</p>
      {total > 0 && (
        <div className="mt-2 h-1.5 bg-white/60 rounded-full overflow-hidden">
          <div className={`h-full ${colorMap.bar}`}
            style={{ width: `${Math.min(100, (used / total) * 100)}%` }} />
        </div>
      )}
    </Card>
  );
}

function ApplyLeaveDialog({ open, onClose, onSaved }) {
  const [type, setType] = useState('annual');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [hours, setHours] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => { setType('annual'); setStart(''); setEnd(''); setHours(''); setReason(''); };

  const handleSubmit = async () => {
    if (!start || !end) { toast.error('Start and end date required'); return; }
    setSaving(true);
    try {
      const body = { leave_type: type, start_date: start, end_date: end, reason };
      if (hours && parseFloat(hours) > 0) body.hours = parseFloat(hours);
      const res = await fetch(`${BACKEND_URL}/api/leave/request`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) { toast.success('Submitted'); reset(); onSaved(); onClose(); }
      else { const e = await res.json().catch(()=>({})); toast.error(e.detail || 'Failed'); }
    } catch (e) { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="apply-leave-dialog">
        <DialogHeader>
          <DialogTitle>Apply for Leave</DialogTitle>
          <DialogDescription>Fill in the dates. Hours will auto-calculate based on weekdays × 7.6h if you leave it blank.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Leave Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="annual">Annual Leave</SelectItem>
                <SelectItem value="sick">Sick Leave</SelectItem>
                <SelectItem value="long_service">Long Service Leave</SelectItem>
                <SelectItem value="unpaid">Unpaid Leave</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start date</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="leave-start-date" />
            </div>
            <div>
              <Label>End date</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="leave-end-date" />
            </div>
          </div>
          <div>
            <Label>Hours (optional — auto-calculated otherwise)</Label>
            <Input type="number" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="leave blank to auto-calc" data-testid="leave-hours" />
          </div>
          <div>
            <Label>Reason (optional)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Brief context for your manager…" data-testid="leave-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="submit-leave-btn">{saving ? 'Submitting…' : 'Submit'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewQueueTab({ role }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [reviewing, setReviewing] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const url = showAll ? `${BACKEND_URL}/api/leave/all` : `${BACKEND_URL}/api/leave/pending`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setItems(d.requests || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [showAll]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold">{showAll ? 'All Requests' : 'Pending Requests'}</h3>
        <Button variant="outline" size="sm" onClick={() => setShowAll(!showAll)} data-testid="toggle-all-leaves">
          {showAll ? 'Show pending only' : 'Show all history'}
        </Button>
      </div>
      {loading ? <p className="text-sm text-slate-500 py-6 text-center">Loading…</p>
        : items.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center">No requests {showAll ? '' : 'pending review'}.</p>
        : (
          <div className="space-y-2">
            {items.map(r => {
              const Icon = STATUS_ICON[r.status] || Clock;
              return (
                <div key={r.request_id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`review-row-${r.request_id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border flex items-center gap-1 ${STATUS_BADGE[r.status]}`}>
                        <Icon className="h-3 w-3" /> {r.status}
                      </span>
                      <span className="font-semibold text-sm">{r.employee_name}</span>
                      <span className="text-xs text-slate-500 capitalize">· {r.leave_type}</span>
                      <span className="text-xs text-slate-500">{r.start_date} → {r.end_date} · {r.hours}h</span>
                    </div>
                    {r.reason && <p className="text-xs text-slate-600 mt-1 italic">"{r.reason}"</p>}
                    {r.review_comments && <p className="text-xs text-blue-700 mt-1">💬 {r.reviewer_name}: {r.review_comments}</p>}
                  </div>
                  {r.status === 'pending' && (
                    <Button size="sm" onClick={() => setReviewing(r)} data-testid={`review-btn-${r.request_id}`}>Review</Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      <ReviewDialog request={reviewing} onClose={() => setReviewing(null)} onDone={() => { setReviewing(null); fetchItems(); }} />
    </Card>
  );
}

function ReviewDialog({ request, onClose, onDone }) {
  const [decision, setDecision] = useState('approved');
  const [comments, setComments] = useState('');
  const [saving, setSaving] = useState(false);
  if (!request) return null;
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/leave/${request.request_id}/review`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, comments }),
      });
      if (res.ok) { toast.success(`Marked ${decision}`); onDone(); }
      else { const e = await res.json().catch(()=>({})); toast.error(e.detail || 'Failed'); }
    } catch (e) { toast.error('Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!request} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="review-leave-dialog">
        <DialogHeader>
          <DialogTitle>Review Leave Request</DialogTitle>
          <DialogDescription>
            {request.employee_name} · {request.leave_type} · {request.hours}h ({request.start_date} → {request.end_date})
          </DialogDescription>
        </DialogHeader>
        {request.reason && <p className="text-sm text-slate-700 italic bg-slate-50 p-3 rounded">"{request.reason}"</p>}
        <div>
          <Label>Decision</Label>
          <Select value={decision} onValueChange={setDecision}>
            <SelectTrigger data-testid="decision-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="approved">Approve</SelectItem>
              <SelectItem value="rejected">Reject</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Comments (optional)</Label>
          <Textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={3} data-testid="review-comments" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="confirm-review-btn">{saving ? 'Saving…' : 'Save Decision'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BalancesTab() {
  const [data, setData] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [edits, setEdits] = useState({ annual: '', sick: '', long_service: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await fetch(`${BACKEND_URL}/api/leave/balances`, { credentials: 'include' });
    if (res.ok) setData(await res.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (b) => {
    setEditingId(b.employee_id);
    setEdits({
      annual: String(b.annual_per_week ?? 0),
      sick: String(b.sick_per_week ?? 0),
      long_service: String(b.long_service_per_week ?? 0),
    });
  };
  const cancelEdit = () => { setEditingId(null); };
  const saveEdit = async (employeeId) => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/leave/balances/${employeeId}/accrual`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          annual_leave_hours_per_week: Number(edits.annual) || 0,
          sick_leave_hours_per_week: Number(edits.sick) || 0,
          long_service_leave_hours_per_week: Number(edits.long_service) || 0,
        }),
      });
      if (res.ok) {
        toast.success('Accrual rates saved');
        setEditingId(null);
        await load();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save');
      }
    } finally { setSaving(false); }
  };

  if (!data) return <Card className="p-6">Loading…</Card>;
  return (
    <Card className="p-5">
      <h3 className="font-semibold mb-3">Leave Balances · {data.year} (YTD-accrued − used)</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-100">
            <tr>
              <th className="p-2 text-left">Employee</th>
              <th className="p-2 text-left">Role</th>
              <th className="p-2 text-right" colSpan={3}>Annual</th>
              <th className="p-2 text-right" colSpan={3}>Sick</th>
              <th className="p-2 text-right" colSpan={3}>Long Service</th>
              <th className="p-2 text-right">Unpaid</th>
              <th className="p-2 text-right">Edit</th>
            </tr>
            <tr className="text-[10px] text-slate-500 bg-slate-50">
              <th></th><th></th>
              <th className="p-1">h/wk</th><th className="p-1">accrued</th><th className="p-1">left</th>
              <th className="p-1">h/wk</th><th className="p-1">accrued</th><th className="p-1">left</th>
              <th className="p-1">h/wk</th><th className="p-1">accrued</th><th className="p-1">left</th>
              <th className="p-1">used</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.balances.map(b => {
              const isEditing = editingId === b.employee_id;
              return (
                <tr key={b.employee_id} className="border-t hover:bg-slate-50" data-testid={`balance-row-${b.employee_id}`}>
                  <td className="p-2 font-medium">{b.employee_name}</td>
                  <td className="p-2 text-slate-600 text-xs capitalize">{b.role.replace('_', ' ')}</td>
                  <td className="p-1 text-right tabular-nums">
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="60"
                        value={edits.annual}
                        onChange={(e) => setEdits({ ...edits, annual: e.target.value })}
                        className="w-16 text-right border border-slate-300 rounded px-1 py-0.5"
                        data-testid={`edit-annual-${b.employee_id}`}
                      />
                    ) : b.annual_per_week}
                  </td>
                  <td className="p-1 text-right tabular-nums">{b.annual_accrued_ytd.toFixed(1)}</td>
                  <td className="p-1 text-right tabular-nums font-semibold">{b.annual_remaining.toFixed(1)}</td>
                  <td className="p-1 text-right tabular-nums">
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="60"
                        value={edits.sick}
                        onChange={(e) => setEdits({ ...edits, sick: e.target.value })}
                        className="w-16 text-right border border-slate-300 rounded px-1 py-0.5"
                        data-testid={`edit-sick-${b.employee_id}`}
                      />
                    ) : b.sick_per_week}
                  </td>
                  <td className="p-1 text-right tabular-nums">{b.sick_accrued_ytd.toFixed(1)}</td>
                  <td className="p-1 text-right tabular-nums font-semibold">{b.sick_remaining.toFixed(1)}</td>
                  <td className="p-1 text-right tabular-nums">
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="60"
                        value={edits.long_service}
                        onChange={(e) => setEdits({ ...edits, long_service: e.target.value })}
                        className="w-16 text-right border border-slate-300 rounded px-1 py-0.5"
                        data-testid={`edit-longservice-${b.employee_id}`}
                      />
                    ) : b.long_service_per_week}
                  </td>
                  <td className="p-1 text-right tabular-nums">{b.long_service_accrued_ytd.toFixed(1)}</td>
                  <td className="p-1 text-right tabular-nums font-semibold">{b.long_service_remaining.toFixed(1)}</td>
                  <td className="p-2 text-right tabular-nums text-slate-500">{b.unpaid_used.toFixed(0)}h</td>
                  <td className="p-1 text-right whitespace-nowrap">
                    {isEditing ? (
                      <div className="flex items-center gap-1 justify-end">
                        <Button
                          size="sm"
                          onClick={() => saveEdit(b.employee_id)}
                          disabled={saving}
                          className="h-7 px-2 text-xs"
                          data-testid={`save-accrual-${b.employee_id}`}
                        >Save</Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={cancelEdit}
                          className="h-7 px-2 text-xs"
                          data-testid={`cancel-accrual-${b.employee_id}`}
                        >Cancel</Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startEdit(b)}
                        className="h-7 px-2 text-xs"
                        data-testid={`edit-accrual-${b.employee_id}`}
                      >Edit</Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
        <FileText className="h-3 w-3" /> Edit h/wk inline — values save immediately to <code className="bg-slate-100 px-1 rounded">mechanic_payroll</code> and the YTD accrual recomputes on next reload.
      </p>
    </Card>
  );
}
